<script lang="ts" setup>
import { ref, onUnmounted, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { formatNumber } from '@/app/core/util/numbers'
import { getErrorCode } from '@/app/core/composable/general'
import { useI18n } from 'vue-i18n'
import PaginatedTable from '@/app/components/PaginatedTable.vue'
import {
  DBtn,
  DTextField,
  DTable,
  DLoading,
  DModal,
  XIcon,
  CheckIcon,
  Loading02Icon,
  FileX02Icon
} from 'front-library'
import {
  getIncreaseSalaryRequests,
  IncreaseSalaryRequest
} from '@/app/core/services/increase-salary-requests'

usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Loading */
const loading = ref(false)
const showModal = ref(false)

/* Datos */
const search = ref()
const tableIncreaseSalaryRequests = ref(null)
const refresh = ref()
const isRefreshData = ref<boolean>(false)

/* Datos componente table */
const columns = ref([
  {
    name: 'user',
    title: t('increaseSalary.list.applicant'),
    table: 'increase_salary_requests.user'
  },
  {
    name: 'increaseReason',
    title: t('increaseSalary.list.reason'),
    table: 'increase_reasons.name'
  },
  {
    name: 'increaseSalaryType',
    title: t('increaseSalary.list.increaseType'),
    table: 'increase_salary_types.name'
  },
  {
    name: 'value',
    title: t('increaseSalary.list.value'),
    table: 'increase_salary_requests.value',
    format: (value: any) => formatNumber(value),
    align: 'end'
  },
  { name: 'listEmployees', title: t('increaseSalary.list.countEmployees') },
  {
    name: 'appliesFrom',
    title: t('increaseSalary.list.appliesFrom'),
    table: 'increase_salary_requests.applies_from'
  },
  {
    name: 'status',
    title: t('increaseSalary.list.state'),
    table: 'process_status.name'
  },
  { name: 'listErrors', title: 'Error' },
  {
    name: 'observation',
    title: t('increaseSalary.list.observations'),
    table: 'increase_salary_requests.observation'
  },
  {
    name: 'created_at',
    title: t('increaseSalary.list.shippingDate'),
    table: 'increase_salary_requests.created_at'
  }
])
const rows = ref<IncreaseSalaryRequest[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Datos componente tableErrors */
const columnsError = ref([
  { key: 'employee', title: t('increaseSalary.list.employee') },
  { key: 'error', title: t('increaseSalary.list.error') }
])
const rowsError = ref<any[]>([])

/* Redirect's */
const createIncreaseSalaryRequest = () => {
  void router.push({ name: 'CreateIncreaseSalaryRequest' })
}

/* Listar solicitudes de aumento salarial */
const requests = async (load: boolean = true) => {
  if (load) loading.value = true
  isRefreshData.value = true
  try {
    const data: any = await getIncreaseSalaryRequests(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data.results
    totalRows.value = data.totalItems
    loading.value = false
    setTimeout(() => {
      isRefreshData.value = false
    }, 2000)
  } catch (error: any) {
    loading.value = false
    const code = getErrorCode(error.response.data?.code)
    notifications.addNotification({
      title: t('error.titles.' + code),
      text: t('error.messages.' + code),
      type: 'error',
      time: 10000
    })
  }
}

/* Cerrar y abrir el componente Modal */
const closeModal = () => {
  showModal.value = false
  rowsError.value = []
}

const openModal = (data: any) => {
  rowsError.value = data
  showModal.value = true  
}

onMounted(() => {
  refresh.value = setInterval(() => {
    void requests(false)
  }, 10000)
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <DLoading v-show="loading" :message="t('loading.data')" />
  <DModal
    :title="t('increaseSalary.list.errors')"
    :open="showModal"
    @close-modal="closeModal"
    size="w-8/12">
    <template #form>
      <PaginatedTable :columns="columnsError" :rows="rowsError" />
    </template>
  </DModal>
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('increaseSalary.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('increaseSalary.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="requests" />
          <DBtn @click.prevent="requests" v-html="t('form.button.search')" />
        </div>
        <DBtn
          :icon="'PlusIcon'"
          type="button"
          @click="createIncreaseSalaryRequest()"
          v-if="store.hasPermission('increase_salary_request:bulk')">
          {{
            t('form.button.create', {
              form: t('increaseSalary.list.title', 0)
            })
          }}
        </DBtn>
      </div>
    </div>
    <DTable
      ref="tableIncreaseSalaryRequests"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), requests()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), requests()">
      <template #status="data: any">
        <div class="flex items-center justify-between gap-2">
          <div>{{ t('status.' + data.statusProcess) }}</div>
          <div v-if="data.statusProcess === 'ERROR'">
            <XIcon class="text-red-500" />
          </div>
          <div v-if="data.statusProcess === 'COMPLETE'">
            <CheckIcon class="text-green-500" />
          </div>
          <div
            class="flex flex-col items-center"
            v-if="data.statusProcess === 'PENDING'">
            <Loading02Icon class="rotate text-blue-600" />
          </div>
        </div>
      </template>
      <template #listEmployees="data: any">
        <div class="flex items-center">
          <div
            v-for="(employee, index) in data.employees.split(',').slice(0, 1)"
            :key="index">
            {{ employee }}
          </div>
          <div
            v-if="data.employees.split(',').length > 1"
            class="bg-blue-50 rounded-lg text-blue-700 text-sm font-semibold p-0.5 ml-2 h-6">
            {{ data.employees.split(',').length - 1 }}+
          </div>
        </div>
      </template>
      <template #listErrors="data: any">
        <div v-if="data.errors && data.errors.length > 0">
          <a @click="openModal(data.errors)">
            <FileX02Icon class="cursor-pointer" />
          </a>
        </div>
      </template>
      <template #refresh v-if="isRefreshData">
        <Loading02Icon class="rotate text-gray-400 mr-2" />
        <h1 class="text-gray-400 text-base" v-html="t('loading.updated')" />
      </template>
    </DTable>
  </div>
</template>
